import { Button, Flex, Tag, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../../hooks/use-date-formatter.tsx"
import { shortRelativeTime } from "../../../../utils/date/short-relative-time.ts"
import {
  ENVELOPE_STATUS_STYLES,
  NEARING_EXPIRY_STYLE,
  OVERDUE_STYLE
} from "../../constants/envelope-statuses.ts"
import {
  parseApiTime,
  requestClock,
  secondsBetween
} from "../../helpers/api-time.ts"
import { isCountersigner } from "../../helpers/countersigner.ts"
import { formatElapsed } from "../../helpers/elapsed.ts"
import type {
  TrackedEnvelope,
  TrackingOrdering
} from "../../types/tracking.types.ts"
import { RecipientLines } from "./RecipientLines.tsx"

const { Text } = Typography
const PILL = "m-0 rounded-full border-0 px-2.5 py-0.5 text-xs font-medium"

const ourDelaySeconds = (row: TrackedEnvelope) => {
  const ours = row.recipients.find(isCountersigner)
  if (!ours?.activity.first_sent) return null
  return secondsBetween(
    ours.activity.first_sent,
    ours.signed ?? requestClock(row.ended_at)
  )
}

export interface TrackingColumnsOptions {
  actions?: ColumnsType<TrackedEnvelope>[number]
  ordering?: TrackingOrdering | null
  onView?: (envelope: TrackedEnvelope) => void
}

const sortOrderFor = (
  ordering: TrackingOrdering | null | undefined,
  field: "sent" | "last_activity"
) => {
  if (ordering === field) return "ascend" as const
  if (ordering === `-${field}`) return "descend" as const
  return null
}

export function useTrackingColumns({
  actions,
  ordering = null,
  onView
}: TrackingColumnsOptions = {}): ColumnsType<TrackedEnvelope> {
  const { t } = useTranslation()
  const { formatDate, dateFormat } = useDateFormatter()

  return useMemo(() => {
    const columns: ColumnsType<TrackedEnvelope> = [
      {
        title: t("esign.tracking.columns.document"),
        key: "document",
        width: 240,
        render: (_, row) => (
          <Flex vertical align={"flex-start"}>
            {onView ? (
              <Button
                type={"link"}
                className={"h-auto max-w-full !p-0 text-left font-semibold"}
                aria-label={t("esign.tracking.viewer.open", {
                  name: row.subject.name
                })}
                onClick={() => onView(row)}
              >
                <Text
                  strong
                  ellipsis={{ tooltip: row.subject.name }}
                  className={"!text-inherit"}
                >
                  {row.subject.name}
                </Text>
              </Button>
            ) : (
              <Text strong ellipsis={{ tooltip: row.subject.name }}>
                {row.subject.name}
              </Text>
            )}
            <Text type={"secondary"} className={"text-xs"}>
              {t(`esign.tracking.documentTypes.${row.subject.type}`)}
            </Text>
          </Flex>
        )
      },
      {
        title: t("esign.tracking.columns.record"),
        key: "record",
        width: 200,
        render: (_, row) =>
          row.record ? (
            <Flex vertical>
              <Text ellipsis>
                {[row.record.uid, row.record.name].filter(Boolean).join(" · ")}
              </Text>
              <Text type={"secondary"} className={"text-xs"}>
                {t(
                  row.record.is_project
                    ? "esign.tracking.record.project"
                    : "esign.tracking.record.lead"
                )}
                {row.record.customer_name
                  ? ` · ${row.record.customer_name}`
                  : ""}
              </Text>
            </Flex>
          ) : (
            <Flex vertical data-testid={`tracking-standalone-${row.id}`}>
              <Text>{t("esign.tracking.record.none")}</Text>
              <Text type={"secondary"} className={"text-xs"}>
                {t("esign.tracking.record.company")}
              </Text>
            </Flex>
          )
      },
      {
        title: t("esign.tracking.columns.recipients"),
        key: "recipients",
        width: 260,
        render: (_, row) => <RecipientLines envelope={row} />
      },
      {
        title: t("esign.tracking.columns.status"),
        key: "status",
        width: 170,
        render: (_, row) => (
          <Flex vertical gap={4} align={"flex-start"}>
            <Tag style={ENVELOPE_STATUS_STYLES[row.status]} className={PILL}>
              {row.stage
                ? t(`esign.tracking.stages.${row.stage}`)
                : t(`esign.statuses.${row.status}`)}
            </Tag>
            {row.overdue && (
              <Tag style={OVERDUE_STYLE} className={PILL}>
                {t("esign.tracking.flags.overdue")}
              </Tag>
            )}
            {row.nearing_expiry && (
              <Tag style={NEARING_EXPIRY_STYLE} className={PILL}>
                {t("esign.tracking.flags.nearingExpiry")}
              </Tag>
            )}
          </Flex>
        )
      },
      {
        title: t("esign.tracking.columns.sent"),
        key: "sent",
        dataIndex: "sent",
        sorter: true,
        sortOrder: sortOrderFor(ordering, "sent"),
        width: 150,
        render: (_, row) => {
          const sent = parseApiTime(row.sent)
          return (
            <Flex vertical>
              <Text>
                {sent ? formatDate(sent.toISOString(), dateFormat) : "—"}
              </Text>
              <Text type={"secondary"} className={"text-xs"}>
                {row.sent_by?.name ?? "—"}
              </Text>
            </Flex>
          )
        }
      },
      {
        title: t("esign.tracking.columns.cycleTime"),
        key: "cycleTime",
        width: 110,
        render: (_, row) =>
          formatElapsed(secondsBetween(row.sent, row.signed_at), t)
      },
      {
        title: t("esign.tracking.columns.ourDelay"),
        key: "ourDelay",
        width: 110,
        render: (_, row) => (
          <Text style={row.ended_at ? undefined : { color: "#B45309" }}>
            {formatElapsed(ourDelaySeconds(row), t)}
          </Text>
        )
      },
      {
        title: t("esign.tracking.columns.reissues"),
        key: "reissues",
        dataIndex: "reissues",
        width: 90,
        render: (value: number) =>
          value ? (
            <Tag className={PILL}>{value}</Tag>
          ) : (
            <Text type={"secondary"}>0</Text>
          )
      },
      {
        title: t("esign.tracking.columns.lastActivity"),
        key: "last_activity",
        dataIndex: "last_activity",
        sorter: true,
        sortOrder: sortOrderFor(ordering, "last_activity"),
        width: 120,
        render: (_, row) => {
          const at = parseApiTime(row.last_activity)
          return at ? (
            <Text ellipsis={{ tooltip: formatDate(at.toISOString()) }}>
              {shortRelativeTime(at)}
            </Text>
          ) : (
            "—"
          )
        }
      }
    ]
    return actions ? [...columns, actions] : columns
  }, [t, formatDate, dateFormat, actions, ordering, onView])
}
